import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import swal from 'sweetalert';
import { logoutUsuario, cambiarMiContrasena } from '../../actions/index.js';
import NavBarHome from '../NavBarHome/NavBarHome.jsx';
import './Perfil.css';

const Perfil = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useSelector(state => state.usuario);

  // ¿Hay que pedir la contraseña actual?
  // Sí, salvo que el usuario haya entrado con Google y nunca haya seteado su propia
  // contraseña (en ese caso se ESTABLECE una nueva sin pedir la actual).
  // Solo se omite pedir la contraseña actual cuando el usuario entró EXPLÍCITAMENTE con
  // Google y todavía no tiene contraseña propia. Cualquier otro caso (login con contraseña,
  // método desconocido por sesión vieja, o ya tiene contraseña propia) la exige.
  const loginMethod = localStorage.getItem('loginMethod');
  const yaTieneContrasenaPropia = !!(user && user.contrasenaPropia);
  const [requiereActual, setRequiereActual] = useState(
    yaTieneContrasenaPropia || loginMethod !== 'google'
  );

  const [contrasenaActual, setContrasenaActual] = useState('');
  const [nuevaContrasena, setNuevaContrasena] = useState('');
  const [repetirContrasena, setRepetirContrasena] = useState('');
  const [enviando, setEnviando] = useState(false);

  const areas = useMemo(() => {
    if (!user) return [];
    return user.areas || user.Areas || [];
  }, [user]);

  const handleLogout = () => {
    dispatch(logoutUsuario());
    history.push('/login');
  };

  const iniciales = useMemo(() => {
    if (!user) return '';
    const a = (user.nombre || '').trim().charAt(0);
    const b = (user.apellidos || '').trim().charAt(0);
    return `${a}${b}`.toUpperCase() || (user.email || '?').charAt(0).toUpperCase();
  }, [user]);

  const formatearFecha = (valor) => {
    if (!valor) return '—';
    const d = new Date(valor);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (enviando) return;

    if (requiereActual && !contrasenaActual) {
      swal('Falta un dato', 'Ingresá tu contraseña actual.', 'warning');
      return;
    }
    if (!nuevaContrasena || nuevaContrasena.length < 8) {
      swal('Contraseña muy corta', 'La nueva contraseña debe tener al menos 8 caracteres.', 'warning');
      return;
    }
    if (nuevaContrasena !== repetirContrasena) {
      swal('No coinciden', 'La nueva contraseña y su repetición no coinciden.', 'warning');
      return;
    }

    setEnviando(true);
    const resultado = await dispatch(
      cambiarMiContrasena(requiereActual ? contrasenaActual : undefined, nuevaContrasena)
    );
    setEnviando(false);

    if (resultado && resultado.ok) {
      swal('Listo', resultado.mensaje || 'Contraseña actualizada.', 'success');
      setContrasenaActual('');
      setNuevaContrasena('');
      setRepetirContrasena('');
      setRequiereActual(true); // a partir de ahora siempre se pide la actual
    } else {
      swal('No se pudo', (resultado && resultado.mensaje) || 'Error al cambiar la contraseña.', 'error');
    }
  };

  return (
    <div className="perfil-container">
      <NavBarHome user={user} handleLogout={handleLogout} />

      <div className="perfil-content">
        <div className="perfil-header">
          <h1>Mi Perfil</h1>
          <p>Tus datos y la configuración de tu cuenta.</p>
        </div>

        <div className="perfil-grid">
          {/* Tarjeta de datos del usuario */}
          <div className="perfil-card perfil-datos">
            <div className="perfil-avatar-row">
              <div className="perfil-avatar">{iniciales}</div>
              <div className="perfil-nombre-bloque">
                <h2>{user ? `${user.nombre || ''} ${user.apellidos || ''}`.trim() : ''}</h2>
                {user && user.perfil && <span className="perfil-chip">{user.perfil}</span>}
              </div>
            </div>

            <div className="perfil-dato">
              <span className="perfil-dato-label">Email</span>
              <span className="perfil-dato-valor">{user ? user.email : '—'}</span>
            </div>
            <div className="perfil-dato">
              <span className="perfil-dato-label">Seniority / Perfil</span>
              <span className="perfil-dato-valor">{user && user.perfil ? user.perfil : '—'}</span>
            </div>
            <div className="perfil-dato">
              <span className="perfil-dato-label">Áreas</span>
              <span className="perfil-dato-valor">
                {areas.length > 0 ? (
                  <span className="perfil-areas">
                    {areas.map(a => (
                      <span key={a.id} className="perfil-area-chip">{a.nombre}</span>
                    ))}
                  </span>
                ) : '—'}
              </span>
            </div>
            <div className="perfil-dato">
              <span className="perfil-dato-label">Estado</span>
              <span className="perfil-dato-valor">
                <span className={`perfil-estado ${user && user.estado ? 'activo' : 'inactivo'}`}>
                  {user && user.estado ? 'Activo' : 'Inactivo'}
                </span>
              </span>
            </div>
            <div className="perfil-dato">
              <span className="perfil-dato-label">Miembro desde</span>
              <span className="perfil-dato-valor">{formatearFecha(user && user.createdAt)}</span>
            </div>
          </div>

          {/* Tarjeta de cambio de contraseña */}
          <div className="perfil-card perfil-password">
            <h2>{requiereActual ? 'Cambiar contraseña' : 'Establecer contraseña'}</h2>
            {!requiereActual && (
              <p className="perfil-info-google">
                Entrás con Google y todavía no tenés una contraseña propia. Podés establecer
                una ahora para también poder ingresar con email y contraseña.
              </p>
            )}

            <form onSubmit={handleSubmit} className="perfil-form">
              {requiereActual && (
                <div className="perfil-form-grupo">
                  <label>Contraseña actual</label>
                  <input
                    type="password"
                    value={contrasenaActual}
                    onChange={(e) => setContrasenaActual(e.target.value)}
                    autoComplete="current-password"
                    placeholder="Tu contraseña actual"
                  />
                </div>
              )}
              <div className="perfil-form-grupo">
                <label>Nueva contraseña</label>
                <input
                  type="password"
                  value={nuevaContrasena}
                  onChange={(e) => setNuevaContrasena(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Mínimo 8 caracteres"
                />
              </div>
              <div className="perfil-form-grupo">
                <label>Repetir nueva contraseña</label>
                <input
                  type="password"
                  value={repetirContrasena}
                  onChange={(e) => setRepetirContrasena(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Repetí la nueva contraseña"
                />
              </div>

              <button type="submit" className="perfil-btn" disabled={enviando}>
                {enviando ? 'Guardando...' : (requiereActual ? 'Actualizar contraseña' : 'Establecer contraseña')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Perfil;
